import { Injectable } from '@angular/core';

import type { BadgeVariant, TagVariant } from '@mercadoni/stark-ui';

import type { NotificationStatus } from '../../interfaces';
import { translateNow, translationVersion } from '../i18n/translate-now';

// Note: Type definitions for process and step statuses
// Can be extracted to models if needed for wider usage

type ProcessStatus = 'completed' | 'error' | 'running';
type StepStatus = 'ok' | 'error' | 'pending';

@Injectable({ providedIn: 'root' })
export class StatusMapperService {
  private static readonly STATUS_BADGES: Record<ProcessStatus, TagVariant> = {
    completed: 'success',
    error: 'danger',
    running: 'info',
  };

  private static readonly STEP_ICONS: Record<StepStatus, string> = {
    ok: 'check',
    error: 'x',
    pending: 'clock',
  };

  private static readonly STEP_COLORS: Record<StepStatus, string> = {
    ok: 'var(--sk-color-success-500)',
    error: 'var(--sk-color-danger-500)',
    pending: 'var(--sk-color-text-disabled)',
  };

  private static readonly NOTIFICATION_BADGES: Record<NotificationStatus, BadgeVariant> = {
    success: 'success',
    error: 'danger',
    warning: 'warning',
    info: 'primary',
  };

  private static readonly NOTIFICATION_ICONS: Record<NotificationStatus, string> = {
    success: 'success',
    error: 'error',
    warning: 'warning',
    info: 'info',
  };

  private static readonly NOTIFICATION_COLORS: Record<NotificationStatus, string> = {
    success: 'var(--sk-color-success-500)',
    error: 'var(--sk-color-danger-500)',
    warning: 'var(--sk-color-warning-500)',
    info: 'var(--sk-color-info-500)',
  };

  private static readonly ASYNC_PROCESS_ICONS: Record<string, string> = {
    pending: 'clock',
    running: 'spinner',
    success: 'success',
    error: 'error',
    info: 'info',
  };

  private static readonly ASYNC_PROCESS_CLASSES: Record<string, string> = {
    pending: 'text-sk-text-secondary',
    running: 'text-status-info-fg',
    success: 'text-status-success-fg',
    error: 'text-status-error-fg',
    info: 'text-status-info-fg',
  };

  private static readonly STATUS_LABEL_KEYS: Record<string, string> = {
    completed: 'common.status.completed',
    error: 'common.status.error',
    running: 'common.status.running',
    ok: 'common.status.ok',
    pending: 'common.status.pending',
  };

  getStatusBadge(status: ProcessStatus): TagVariant {
    return StatusMapperService.STATUS_BADGES[status] ?? 'default';
  }

  getStepIcon(status: StepStatus): string {
    return StatusMapperService.STEP_ICONS[status] ?? 'circle';
  }

  getStepColor(status: StepStatus): string {
    return StatusMapperService.STEP_COLORS[status] ?? 'var(--sk-color-text-disabled)';
  }

  getStatusLabel(status: string): string {
    translationVersion(); // templates that call this follow language changes
    const key = StatusMapperService.STATUS_LABEL_KEYS[status];
    return key ? translateNow(key) : status;
  }

  /** Badge variant for the notifications-drawer / server response modal status vocabulary. */
  getNotificationBadge(status: NotificationStatus): BadgeVariant {
    return StatusMapperService.NOTIFICATION_BADGES[status];
  }

  /** Icon name for the notifications-drawer status vocabulary. */
  getNotificationIcon(status: NotificationStatus): string {
    return StatusMapperService.NOTIFICATION_ICONS[status];
  }

  /** CSS color var for the server response modal status dot. */
  getNotificationColor(status: NotificationStatus): string {
    return StatusMapperService.NOTIFICATION_COLORS[status];
  }

  /** Icon name for the async-process-viewer status vocabulary (pending/running/success/error/info). */
  getAsyncProcessIcon(status: string): string {
    return StatusMapperService.ASYNC_PROCESS_ICONS[status] ?? 'circle';
  }

  /** Tailwind text-color class for the async-process-viewer status vocabulary. */
  getProcessStatusClass(status: string): string {
    return StatusMapperService.ASYNC_PROCESS_CLASSES[status] ?? 'text-sk-text-secondary';
  }
}
